/**
 * Acquiring and releasing capture sources.
 *
 * The display stream is the expensive one: getting it costs the user a
 * permission dialog and a source picker. Everything in the recorder is built
 * around holding onto it — restart tears down the MediaRecorder and never the
 * stream, which is the single behaviour that made us abandon Cap.
 */

export type SourceKind = 'display' | 'mic' | 'camera';

export class CaptureDenied extends Error {
  readonly kind: SourceKind;

  constructor(kind: SourceKind, cause: unknown) {
    super(messageFor(kind, cause));
    this.name = 'CaptureDenied';
    this.kind = kind;
    this.cause = cause;
  }
}

function messageFor(kind: SourceKind, cause: unknown): string {
  const name = cause instanceof DOMException ? cause.name : '';
  const label = kind === 'display' ? 'screen' : kind === 'mic' ? 'microphone' : 'camera';

  switch (name) {
    case 'NotAllowedError':
      return kind === 'display'
        ? 'Screen sharing was cancelled. Pick a screen, window, or tab to start recording.'
        : `Slate needs permission to use your ${label}. Allow it in your browser's site settings and try again.`;
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return `No ${label} was found on this device.`;
    case 'NotReadableError':
      return `Your ${label} is in use by another app. Close it and try again.`;
    case 'OverconstrainedError':
      return `Your ${label} does not support the settings Slate asked for.`;
    default:
      return `Could not start the ${label}.`;
  }
}

const DISPLAY_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {
    frameRate: { ideal: 30, max: 30 },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
  // Tab and (on Chromium) system audio. Harmless where unsupported: the
  // browser simply returns a stream with no audio track.
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  },
};

export async function requestDisplay(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getDisplayMedia(DISPLAY_CONSTRAINTS);
  } catch (error) {
    throw new CaptureDenied('display', error);
  }
}

export async function requestMic(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (error) {
    throw new CaptureDenied('mic', error);
  }
}

export async function requestCamera(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 },
        facingMode: 'user',
      },
    });
  } catch (error) {
    throw new CaptureDenied('camera', error);
  }
}

export function stopStream(stream: MediaStream | null): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      // Already stopped; nothing to do.
    }
  }
}

export interface InputDevice {
  deviceId: string;
  label: string;
}

/**
 * Device labels are blank until a permission has been granted at least once,
 * so this is worth calling again after the first successful getUserMedia.
 */
export async function listDevices(kind: 'audioinput' | 'videoinput'): Promise<InputDevice[]> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === kind)
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `${kind === 'audioinput' ? 'Microphone' : 'Camera'} ${index + 1}`,
      }));
  } catch {
    return [];
  }
}

/** Track dimensions, falling back to 1080p when the browser will not say. */
export function trackSize(track: MediaStreamTrack | undefined): { width: number; height: number } {
  const settings = track?.getSettings();
  return {
    width: settings?.width && settings.width > 0 ? settings.width : 1920,
    height: settings?.height && settings.height > 0 ? settings.height : 1080,
  };
}
